import { useEffect, useMemo, useState } from 'react';
import { App as AntApp } from 'antd';
import AppBar from '../components/AppBar';
import { RM } from '../components/kit';
import * as earningsApi from '../api/earnings';
import * as teamApi from '../api/team';

// ─────────────────────────────────────────────────────────────────────────
// Client-side what-if math. Mirrored VERBATIM from cid-agent-server
// src/services/simulate.service.js (which itself mirrors the production
// commission engine) so these live slider estimates equal the server's
// POST /simulate/* results without a network round-trip. Nothing is saved.
// ─────────────────────────────────────────────────────────────────────────
const GCI_TIER_THRESHOLDS = [
  { tier: 60, minGci: 0 },
  { tier: 65, minGci: 25000 },
  { tier: 70, minGci: 50000 },
  { tier: 75, minGci: 100000 },
  { tier: 80, minGci: 200000 },
  { tier: 85, minGci: 300000 },
];
const LIFETIME_85 = 300000;
const STEP_87 = 250000;
const STEP_90 = 500000;
const GAP_RATES = { 60: 0.16, 65: 0.13, 70: 0.11, 75: 0.08, 80: 0.04 };
const STANDARD_RATES = [0.02, 0.01, 0.01, 0.01];
const REDUCED_RATES = [0.004, 0.003, 0.002, 0.001];

const num = (v) => (v === '' || v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
const clampPct = (v) => Math.max(0, Math.min(100, Math.round(v)));

// Pure GCI-tier computation (gci.service.computeGciTier).
function computeGciTier(lifetime, currentYear) {
  const lt = num(lifetime);
  const cy = num(currentYear);
  if (lt < LIFETIME_85) {
    let tier = 60;
    for (const t of GCI_TIER_THRESHOLDS) if (lt >= t.minGci) tier = t.tier;
    return tier;
  }
  const lifetimeAtYearStart = lt - cy;
  const progressSince85 = lifetimeAtYearStart >= LIFETIME_85 ? cy : Math.max(0, lt - LIFETIME_85);
  if (progressSince85 >= STEP_90) return 90;
  if (progressSince85 >= STEP_87) return 87;
  return 85;
}

// How much more GCI reaches the next tier from (lifetime, currentYear).
function computeGapToNext(lifetime, currentYear, reachedTier) {
  const lt = num(lifetime);
  const cy = num(currentYear);
  if (reachedTier < 85) {
    const next = GCI_TIER_THRESHOLDS.find((t) => t.minGci > lt);
    if (!next) return { nextTier: 85, gapToNext: Math.max(0, LIFETIME_85 - lt), basis: 'lifetime' };
    return { nextTier: next.tier, gapToNext: Math.max(0, next.minGci - lt), basis: 'lifetime' };
  }
  const lifetimeAtYearStart = lt - cy;
  const progressSince85 = lifetimeAtYearStart >= LIFETIME_85 ? cy : Math.max(0, lt - LIFETIME_85);
  if (reachedTier === 85) return { nextTier: 87, gapToNext: Math.max(0, STEP_87 - progressSince85), basis: 'year' };
  if (reachedTier === 87) return { nextTier: 90, gapToNext: Math.max(0, STEP_90 - progressSince85), basis: 'year' };
  return { nextTier: null, gapToNext: 0, basis: 'top' };
}

// Progress within the current tier band toward the next tier (for the bar).
function tierPct(lifetime, currentYear, reachedTier, nextTier, basis) {
  if (nextTier == null) return 100;
  const lt = num(lifetime);
  const cy = num(currentYear);
  if (basis === 'lifetime') {
    let curMin = 0;
    for (const t of GCI_TIER_THRESHOLDS) if (lt >= t.minGci) curMin = t.minGci;
    const nextMin = nextTier === 85 ? LIFETIME_85 : GCI_TIER_THRESHOLDS.find((t) => t.tier === nextTier)?.minGci ?? curMin;
    return nextMin > curMin ? clampPct(((lt - curMin) / (nextMin - curMin)) * 100) : 100;
  }
  const lifetimeAtYearStart = lt - cy;
  const progressSince85 = lifetimeAtYearStart >= LIFETIME_85 ? cy : Math.max(0, lt - LIFETIME_85);
  const lower = reachedTier === 85 ? 0 : STEP_87;
  const upper = reachedTier === 85 ? STEP_87 : STEP_90;
  return upper > lower ? clampPct(((progressSince85 - lower) / (upper - lower)) * 100) : 100;
}

function tierMessage(reachedTier, nextTier, gapToNext, basis) {
  if (nextTier == null) return "You're at the top tier — 90%. Nothing more to unlock.";
  if (gapToNext <= 0) return `You've reached the ${reachedTier}% tier.`;
  if (basis === 'year') return `Closing ${RM(gapToNext)} more GCI this year reaches the ${nextTier}% tier.`;
  return `Earning ${RM(gapToNext)} more lifetime GCI reaches the ${nextTier}% tier.`;
}

// "Faiz Abdullah" → "Faiz A." for override-preview labels.
const shortName = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return 'Agent';
  const p = s.split(/\s+/);
  return p.length === 1 ? p[0] : `${p[0]} ${p[1][0].toUpperCase()}.`;
};

// Generation unlock (checkGenerationUnlock) — permissive across both rule sets
// when the caller's recruit config isn't in the fetched payload.
function genUnlocked(gen, counts) {
  if (gen === 1) return true;
  if (!counts) return true;
  const da = Number(counts.directActive ?? counts.directActiveRecruits ?? counts.direct_active_recruits_count);
  const pa = Number(counts.postApril ?? counts.postApril2026 ?? counts.post_april_2026_recruits_count);
  if (Number.isNaN(da) && Number.isNaN(pa)) return true;
  return (!Number.isNaN(da) && da >= gen) || (!Number.isNaN(pa) && pa >= gen - 1);
}

// Normalise the /team/tree payload (flat `downline` list or a nested tree).
function extractDownline(tree) {
  const norm = (m, fallbackGen) => ({
    name: m.name || m.full_name || m.display_name || '',
    gen: Number(m.gen ?? m.generation ?? fallbackGen ?? 1),
    tier: Number(m.tier ?? m.gci_tier ?? 60),
  });
  if (!tree) return [];
  if (Array.isArray(tree)) return tree.map((m) => norm(m));
  if (Array.isArray(tree.downline)) return tree.downline.map((m) => norm(m));
  const out = [];
  const walk = (nodes, gen) => {
    for (const n of nodes || []) {
      out.push(norm(n, gen));
      walk(n.children || n.team || n.downline || [], (Number(n.generation ?? n.gen ?? gen) || gen) + 1);
    }
  };
  walk(tree.children || tree.team || [], 1);
  return out;
}

export default function SimsPage() {
  const { message } = AntApp.useApp();
  // Real seed context (agent's own numbers) so the what-ifs start from truth.
  const [ctx, setCtx] = useState(null); // null while loading

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      earningsApi.listEarningsSummary().then((r) => r?.data ?? r),
      teamApi.listTeamTree().then((r) => r?.data ?? r),
    ])
      .then(([sumR, treeR]) => {
        if (!alive) return;
        const sum = sumR.status === 'fulfilled' ? sumR.value || {} : {};
        const tree = treeR.status === 'fulfilled' ? treeR.value || {} : {};
        if (sumR.status === 'rejected' && treeR.status === 'rejected') {
          message.error('Could not load your numbers — using blank estimates.');
        }
        setCtx({
          lifetime: num(sum.lifetimeGci ?? sum.lifetime_accumulated_gci ?? sum.lifetime),
          currentYear: num(sum.ytdGci ?? sum.currentYearGci ?? sum.current_year_gci),
          tier: Number(sum.tier ?? sum.gci_tier) || 80,
          downline: extractDownline(tree),
          counts: tree?.counts || null,
        });
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const seed = ctx || { lifetime: 0, currentYear: 0, tier: 80, downline: [], counts: null };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Simulator" sub="pure what-if · nothing is saved" />
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <TierSim seed={seed} />
        <CommissionSim seed={seed} />
        <OverrideSim seed={seed} />
        <div style={{ fontSize: 11.5, color: 'var(--muted)', textAlign: 'center', padding: '2px 0 6px' }}>
          Live estimates — pure what-if, nothing is saved.
        </div>
        <div style={{ height: 6 }} />
      </div>
    </div>
  );
}

// ─── Tier ─────────────────────────────────────────────────────────────
function TierSim({ seed }) {
  const [add, setAdd] = useState(50000);

  const r = useMemo(() => {
    const projLifetime = seed.lifetime + add;
    const projCurrentYear = seed.currentYear + add; // new closings land in-year too
    const reachedTier = computeGciTier(projLifetime, projCurrentYear);
    const { nextTier, gapToNext, basis } = computeGapToNext(projLifetime, projCurrentYear, reachedTier);
    return {
      projLifetime,
      reachedTier,
      pct: tierPct(projLifetime, projCurrentYear, reachedTier, nextTier, basis),
      msg: tierMessage(reachedTier, nextTier, gapToNext, basis),
    };
  }, [seed.lifetime, seed.currentYear, add]);

  return (
    <Card title="GCI tier simulator">
      <SliderRow label="If I close additional GCI of" valueF={RM(add)}
        min={0} max={300000} step={5000} value={add} onChange={setAdd} />
      <div style={{ background: 'var(--chip)', borderRadius: 12, padding: '14px 15px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12.5, color: 'var(--primary)', fontWeight: 500 }}>Projected lifetime GCI {RM(r.projLifetime)}</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--primary)' }}>{r.reachedTier}% tier</span>
        </div>
        <div style={{ height: 5, borderRadius: 999, background: 'color-mix(in srgb,var(--primary) 18%,transparent)', overflow: 'hidden' }}>
          <div style={{ width: `${r.pct}%`, height: '100%', background: 'var(--primary)', transition: 'width .18s ease' }} />
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--primary)', opacity: 0.85 }}>{r.msg}</div>
      </div>
    </Card>
  );
}

// ─── Commission ───────────────────────────────────────────────────────
function CommissionSim({ seed }) {
  const [price, setPrice] = useState(650000);
  const [rate, setRate] = useState(2);
  const tier = seed.tier || 80;

  const r = useMemo(() => {
    const gci = price * (rate / 100);
    const personal = gci * (tier / 100);
    const hq = gci * 0.05;
    const overrideRate = tier <= 80 ? GAP_RATES[tier] || 0 : tier === 85 ? 0.02 : 0.004;
    const override = gci * overrideRate;
    const branch = Math.max(0, gci - personal - hq - override);
    return { gci, personal, breakdown: [
      { label: `Personal (${tier}%)`, amount: personal },
      { label: 'HQ royalty (5%)', amount: hq },
      { label: 'Override up-line', amount: override },
      { label: 'Branch / CD', amount: branch },
    ] };
  }, [price, rate, tier]);

  return (
    <Card title="Commission estimator">
      <SliderRow label="Prospective price" valueF={RM(price)}
        min={200000} max={3000000} step={50000} value={price} onChange={setPrice} />
      <SliderRow label="Commission rate" valueF={`${rate}%`}
        min={1} max={3.5} step={0.25} value={rate} onChange={setRate} />
      <div style={{ background: 'var(--chip)', borderRadius: 12, padding: '14px 15px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--primary)' }}>
          <span>Deal GCI</span><b>{RM(r.gci)}</b>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--primary)' }}>
          <span style={{ fontWeight: 500 }}>My share @ {tier}% tier</span>
          <span style={{ fontWeight: 700 }}>{RM(r.personal)}</span>
        </div>
      </div>
      {/* Full split estimate (preserved from the previous commission breakdown). */}
      <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        {r.breakdown.map((b, i) => (
          <div key={i} style={rowLine(i === r.breakdown.length - 1)}>
            <span style={{ color: 'var(--muted)' }}>{b.label}</span>
            <b>{RM(b.amount)}</b>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Override preview ─────────────────────────────────────────────────
function OverrideSim({ seed }) {
  const [gci, setGci] = useState(40000);

  const { rows, total } = useMemo(() => {
    const out = [];
    for (const m of seed.downline || []) {
      const gen = Number(m.gen) || 1;
      if (gen > 4 || !genUnlocked(gen, seed.counts)) continue;
      const rates = Number(m.tier) >= 87 ? REDUCED_RATES : STANDARD_RATES;
      const rate = rates[gen - 1] || 0;
      if (rate <= 0) continue;
      out.push({ label: `Gen ${gen} · ${shortName(m.name)}`, amount: gci * rate });
    }
    return { rows: out, total: out.reduce((s, o) => s + o.amount, 0) };
  }, [seed.downline, seed.counts, gci]);

  return (
    <Card title="Override preview · my downline">
      <SliderRow label="If each closes GCI of" valueF={RM(gci)}
        min={5000} max={100000} step={5000} value={gci} onChange={setGci} />
      <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        {rows.length === 0 && (
          <div style={{ padding: '12px 13px', fontSize: 12.5, color: 'var(--muted)', textAlign: 'center' }}>
            No unlocked downline to preview
          </div>
        )}
        {rows.map((o, i) => (
          <div key={i} style={rowLine(false)}>
            <span>{o.label}</span>
            <b>{RM(o.amount)}</b>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 13px', fontSize: 13, background: 'var(--chip)', color: 'var(--primary)' }}>
          <span style={{ fontWeight: 600 }}>Total override</span>
          <span style={{ fontWeight: 700 }}>{RM(total)}</span>
        </div>
      </div>
    </Card>
  );
}

// ─── shared bits ──────────────────────────────────────────────────────
const Card = ({ title, children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
    <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
    {children}
  </div>
);

const SliderRow = ({ label, valueF, min, max, step, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--muted)' }}>
      <span>{label}</span>
      <b style={{ color: 'var(--text)' }}>{valueF}</b>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ width: '100%', accentColor: 'var(--primary)' }}
    />
  </div>
);

const rowLine = (last) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '13px 15px',
  borderBottom: last ? 'none' : '1px solid var(--border)',
  fontSize: 12.5,
});
